import { Controller } from "stimulus"
import { get, patch } from '@rails/request.js'

export default class extends Controller {
  static targets = ['maskArea', 'member', 'unreadCommentCount', 'unreadActionCount', 'unreadSystemCount', 'unreadCount',
                    'commentNotificationsBtn', 'commentNotificationsSection',
                    'actionNotificationsBtn', 'actionNotificationsSection',
                    'systemNotificationsBtn', 'systemNotificationsSection',
                    'loading']

  static values = {
    readUrl: String,
    notificationsUrl: String,
    countsUrl: String,
    unreadCommentCount: Number,
    unreadActionCount: Number,
    unreadSystemCount: Number,
    unreadCount: Number,
    displayType: String,
    loaded: Boolean
  }

  connect() {
    this.displayTypeValue = 'comment'
    this.loadedValue = false

    document.body.addEventListener('click', () => {
      this.hiddenDisplay()
    })

    window.addEventListener('closeAllDropdowns', this.hiddenDisplayBound = this.hiddenDisplay.bind(this))

    // 頁面載入時先取得未讀數量
    this.loadCounts()
  }

  async loadCounts() {
    if (!this.countsUrlValue) return

    const response = await get(this.countsUrlValue)
    if (!response.ok) return

    const data = await response.json
    this.unreadCountValue = data.unread_count || 0
    this.unreadCommentCountValue = data.unread_comment_count || 0
    this.unreadActionCountValue = data.unread_action_count || 0
    this.unreadSystemCountValue = data.unread_system_count || 0
  }

  disconnect() {
    window.removeEventListener('closeAllDropdowns', this.hiddenDisplayBound)
  }

  async loadNotifications() {
    if (this.loadedValue || !this.notificationsUrlValue) return

    const response = await get(this.notificationsUrlValue)
    if (!response.ok) return

    const html = await response.html
    this.processLoadedNotifications(html)
    this.loadedValue = true
  }

  processLoadedNotifications(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const data = doc.querySelector('#notifications-data')
    if (!data) return

    // 先更新 section 內容
    this.updateSectionContent(doc, '#async-comment-notifications', this.commentNotificationsSectionTarget)
    this.updateSectionContent(doc, '#async-action-notifications', this.actionNotificationsSectionTarget)
    this.updateSectionContent(doc, '#async-system-notifications', this.systemNotificationsSectionTarget)

    // 根據實際顯示的 DOM items 數量更新 counts
    const commentCount = this.commentNotificationsSectionTarget.querySelectorAll('.notification-item').length
    const actionCount = this.actionNotificationsSectionTarget.querySelectorAll('.notification-item').length
    const systemCount = this.systemNotificationsSectionTarget.querySelectorAll('.notification-item').length

    this.unreadCommentCountValue = commentCount
    this.unreadActionCountValue = actionCount
    this.unreadSystemCountValue = systemCount
    this.unreadCountValue = commentCount + actionCount + systemCount

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }

    this.bindNotificationEvents()
    this.displayTypeValueChanged()
  }

  updateSectionContent(doc, selector, target) {
    const section = doc.querySelector(selector)
    if (section && target) {
      target.innerHTML = section.innerHTML
    }
  }

  bindNotificationEvents() {
    const readAllBtns = this.element.querySelectorAll('.notify-wrap .readall')
    readAllBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.readAllBtnClicked(e)
      })
    })

    const notificationLinks = this.element.querySelectorAll('a.notification-link, .notification-link > a')
    notificationLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.notificationItemClicked(link)
      })
    })
  }

  unreadCountValueChanged() {
    const count = this.unreadCountValue
    const displayText = count > 9 ? '9+' : count

    this.unreadCountTargets.forEach(target => {
      target.classList.toggle('hidden', count === 0)
      target.innerText = displayText
    })
  }

  displayTypeValueChanged() {
    const readAll = this.element.querySelector('.readall')
    if (!readAll) return

    const unreadCounts = {
      comment: this.unreadCommentCountValue,
      action: this.unreadActionCountValue,
      system: this.unreadSystemCountValue
    }

    const hasUnread = unreadCounts[this.displayTypeValue] > 0
    readAll.classList.toggle('hidden', !hasUnread)
  }

  async sendReadAllRequest() {
    const items = this.memberTargets.filter(ele =>
      ele.dataset.notificationType === this.displayTypeValue
    )
    const unreadIds = items.map(ele => ele.dataset.id)

    if (unreadIds.length === 0) return

    const resp = await patch(this.readUrlValue, { body: JSON.stringify({ ids: unreadIds }) })
    if (!resp.ok) return

    this.unreadCountValue -= unreadIds.length
    this[`unread${this.capitalize(this.displayTypeValue)}CountValue`] = 0

    const section = `#unread_${this.displayTypeValue}_notifications-settings`
    this.element.querySelectorAll(`${section} .notification-item`).forEach(el => el.remove())
    this.element.querySelectorAll(`${section} hr.border-grey-3`).forEach(el => el.remove())

    const readAllBtn = this.element.querySelector('.readall')
    if (readAllBtn) readAllBtn.classList.add('hidden')

    const emptyMsg = this.element.querySelector(`${section} .unread-notification-empty-msg`)
    if (emptyMsg) emptyMsg.classList.remove('hidden')
  }

  async sendReadRequest(link) {
    const ele = link.closest('.notification-item')
    const resp = await patch(this.readUrlValue, { body: JSON.stringify({ ids: [ele.dataset.id] }) })
    if (!resp.ok) return

    ele.dataset.readat = Date.now()
    window.location = link.href
  }

  notifyBtnClicked(e) {
    e.preventDefault()
    e.stopPropagation()

    // 廣播關閉所有 dropdown
    window.dispatchEvent(new Event('closeAllDropdowns'))

    // 載入通知（如果還沒載入）
    this.loadNotifications()

    // 再打開自己
    this.toggleDisplay()
  }

  readAllBtnClicked(e) {
    this.sendReadAllRequest()
  }

  notificationItemClicked(link) {
    this.sendReadRequest(link)
  }

  hiddenDisplay() {
    this.element.classList.add('close')
    if (this.hasMaskAreaTarget) {
      this.maskAreaTarget.classList.add('hidden')
    }
  }

  toggleDisplay() {
    this.element.classList.toggle('close')
    if (this.hasMaskAreaTarget) {
      this.maskAreaTarget.classList.toggle('hidden')
    }
  }

  hiddenModal() {
    const modal = document.querySelector('input.dropdown')
    if (modal) {
      modal.checked = false
    }
  }

  capitalize(str) {
    return str.replace(/^\w/, (c) => c.toUpperCase());
  }

  switchToCommentNotifications(e) {
    this.switchToTab(e, 'comment')
  }

  switchToActionNotifications(e) {
    this.switchToTab(e, 'action')
  }

  switchToSystemNotifications(e) {
    this.switchToTab(e, 'system')
  }

  switchToTab(e, type) {
    e.preventDefault()
    e.stopPropagation()

    this.displayTypeValue = type

    const tabs = ['comment', 'action', 'system']
    tabs.forEach(tab => {
      const isActive = tab === type
      this[`${tab}NotificationsBtnTarget`].classList.toggle('active', isActive)
      this[`${tab}NotificationsSectionTarget`].classList.toggle('hidden', !isActive)
    })
  }
}
